import { CallAPI } from "../../apiManager/ApiCall";
import { lectureLabAdminMaterialsURL } from "apiManager/APIConfig";

/*
  Admin Copilot Usage — list every user's lecture-lab uploads, latest
  first. Server enforces role=admin; we still pass through whatever the
  server hands back (rows + pagination meta) so the dashboard can render
  page counts, totals and a row-per-material without doing any client-side
  paging on the full dataset.

  Args:
    page         1-based page index             (default 1)
    limit        items per page                 (default 25, max 100)
    status       optional status filter         (queued|processing|ready|failed)
    sourceType   optional source-type filter    (pdf|ppt|image|audio)
*/
export const GetAdminCopilotMaterialsCall = async ({
  page = 1,
  limit = 25,
  status = null,
  sourceType = null,
} = {}) => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (status) params.set("status", String(status));
  if (sourceType) params.set("sourceType", String(sourceType));

  try {
    const response = await CallAPI({
      url: `${lectureLabAdminMaterialsURL}?${params.toString()}`,
      method: "GET",
    });
    if (response?.status === 200 && Array.isArray(response?.materials)) {
      return {
        materials: response.materials,
        pagination: response.pagination || {
          page,
          limit,
          total: response.materials.length,
          totalPages: 1,
        },
      };
    }
    return {
      materials: [],
      pagination: { page, limit, total: 0, totalPages: 1 },
    };
  } catch (_) {
    return {
      materials: [],
      pagination: { page, limit, total: 0, totalPages: 1 },
    };
  }
};

/*
  Admin Copilot Usage — full detail for one material, including the
  cached study pack so the admin can preview the exact tabs the user sees
  in the mobile app (summary, key structures, images, quiz, flashcards).
  Returns `null` on failure; `studyPack` is `null` until the pipeline
  reaches "ready".
*/
export const GetAdminCopilotMaterialCall = async (id) => {
  if (!id) return null;
  try {
    const response = await CallAPI({
      url: `${lectureLabAdminMaterialsURL}/${id}`,
      method: "GET",
    });
    if (response?.status === 200 && response?.material) {
      return {
        material: response.material,
        studyPack: response.studyPack || null,
        ready: !!response.ready,
      };
    }
    return null;
  } catch (_) {
    return null;
  }
};
